import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Button, Input, Table, Typography, Modal, Select, message, Tag, Alert } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import axios from 'axios';
import { AuthContext } from "../context/AuthContext"
import ViewNocForm from "./ViewNocForm";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Department IDs used to gate section visibility / approver lists.
// (Business rule from the original implementation — kept as named
// constants so they're not "magic numbers" scattered through the JSX.)
const DEPT_ID = {
    HR: 25,
    IT: 12,
    FINANCE: 14,
};

const STATUS_LABEL = {
    DRAFT: { text: 'Draft', color: 'default' },
    IN_PROGRESS: { text: 'In Progress', color: 'blue' },
    PENDING_FINAL_APPROVAL: { text: 'Pending Final Approval', color: 'gold' },
    COMPLETED: { text: 'Completed', color: 'green' },
    REJECTED: { text: 'Rejected', color: 'red' },
};

export default function NocForm({ employee, backurl, bearerToken, requestId }) {

    const { authData, backUrl } = useContext(AuthContext);

    const [formData, setFormData] = useState({
        address: '',
        phone: '',
    });

    const [clearanceFormData, setClearanceFormData] = useState(null);
    const [view, setView] = useState(false);

    // Row data per section (each item carries its own `id`, which is the
    // *item's* DB id — not to be confused with the section id below).
    const [deptRows, setDeptRows] = useState([]);
    const [itRows, setItRows] = useState([]);
    const [financeRows, setFinanceRows] = useState([]);
    const [hrRows, setHrRows] = useState([]);

    // FIX: these were previously missing entirely. Standalone item
    // endpoints (dept-items/it-items/...) require the *section's own*
    // primary key in the `section` field — the old code was sending a
    // row's serial number (1, 2, 3...) instead, which either failed or
    // silently corrupted data.
    const [sectionIds, setSectionIds] = useState({ dept: null, it: null, finance: null, hr: null });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exitInterviewLoading, setExitInterviewLoading] = useState(false);
    const [clearanceData, setClearanceData] = useState({
        correspondence_address: '',
        assigned_dept_head: null,
        assigned_it_approver: null,
        assigned_finance_approver: null,
        assigned_hr_approver: null
    });

    const currentDate = new Date().toLocaleDateString("en-GB");

    // FIX: `requestId` may not be known yet if the parent hasn't loaded it,
    // so fall back to whatever the clearance form fetch resolves to. This
    // replaces the old broken `rid`/`getRequestId` logic entirely.
    const activeRequestId = requestId || clearanceFormData?.id;

    const authHeaders = {
        headers: {
            Authorization: `Bearer ${authData?.access}`,
            'Content-Type': 'application/json',
        }
    };

    // ---------------- Fetchers ----------------

    // FIX: log what actually comes back so it's obvious in devtools whether
    // the call fired, returned nothing, or returned a shape that doesn't
    // match what the dropdowns expect.
    const fetchEmployees = useCallback(async () => {
        if (!backUrl) {
            console.warn('fetchEmployees skipped: backUrl is not set yet.');
            return;
        }
        try {
            const response = await axios.get(`${backUrl}/api/emp/employees`, authHeaders);
            const results = response.data?.results ?? response.data ?? [];
            console.log(`fetchEmployees: got ${results.length} employee(s). Sample:`, results[0]);
            setEmployees(results);
        } catch (error) {
            console.error('Error fetching employees:', error?.response?.status, error?.response?.data || error.message);
            message.error('Error fetching employees');
        }
    }, [backUrl, authData?.access]);

    const fetchClearanceForm = useCallback(async () => {
        try {
            const response = await axios.get(
                `${backUrl}/api/clearance/requests/?employee=${employee.id}`,
                authHeaders
            );
            if (response.data.results && response.data.results.length > 0) {
                setClearanceFormData(response.data.results[0]);
            } else {
                setClearanceFormData(null);
            }
        } catch (error) {
            console.error('Error fetching clearance form:', error);
        }
    }, [backUrl, authData?.access, employee?.id]);

    const fetchDeptItems = useCallback(async (id) => {
        if (!id) return;
        try {
            const res = await axios.get(`${backUrl}/api/clearance/dept-section/${id}/`, authHeaders);
            setSectionIds(prev => ({ ...prev, dept: res.data.id }));
            setDeptRows((res.data.items || []).map((item, index) => ({
                key: String(item.id ?? index),
                id: item.id,
                item_name: item.item_name,
                status: item.status || '',
            })));
        } catch (error) {
            console.error('Error fetching dept items:', error?.response?.status, error?.response?.data || error.message);
        }
    }, [backUrl, authData?.access]);

    const fetchItItems = useCallback(async (id) => {
        if (!id) return;
        try {
            const res = await axios.get(`${backUrl}/api/clearance/it-section/${id}/`, authHeaders);
            setSectionIds(prev => ({ ...prev, it: res.data.id }));
            // FIX: IT items use `response_value`, not `status`.
            setItRows((res.data.items || []).map((item, index) => ({
                key: String(item.id ?? index),
                id: item.id,
                item_name: item.item_name,
                status: item.response_value || '',
            })));
        } catch (error) {
            console.error('Error fetching IT items:', error?.response?.status, error?.response?.data || error.message);
        }
    }, [backUrl, authData?.access]);

    const fetchFinanceItems = useCallback(async (id) => {
        if (!id) return;
        try {
            const res = await axios.get(`${backUrl}/api/clearance/finance-section/${id}/`, authHeaders);
            setSectionIds(prev => ({ ...prev, finance: res.data.id }));
            // FIX: Finance items use `deduction_amount`, not `amount`.
            setFinanceRows((res.data.items || []).map((item, index) => ({
                key: String(item.id ?? index),
                id: item.id,
                item_name: item.item_name,
                amount: item.deduction_amount ?? '',
            })));
        } catch (error) {
            console.error('Error fetching finance items:', error?.response?.status, error?.response?.data || error.message);
        }
    }, [backUrl, authData?.access]);

    const fetchHrItems = useCallback(async (id) => {
        if (!id) return;
        try {
            const res = await axios.get(`${backUrl}/api/clearance/hr-section/${id}/`, authHeaders);
            setSectionIds(prev => ({ ...prev, hr: res.data.id }));
            setHrRows((res.data.items || []).map((item, index) => ({
                key: String(item.id ?? index),
                id: item.id,
                item_name: item.item_name,
                status: item.status || '',
            })));
        } catch (error) {
            console.error('Error fetching HR items:', error?.response?.status, error?.response?.data || error.message);
        }
    }, [backUrl, authData?.access]);

    useEffect(() => {
        fetchEmployees();
        fetchClearanceForm();
    }, [fetchEmployees, fetchClearanceForm]);

    // FIX: previously all four fetches ran once on mount, before the
    // clearance request id was even known, so they usually fetched nothing.
    // Now they (re-)run whenever we actually have a valid request id.
    useEffect(() => {
        if (!activeRequestId) return;
        fetchDeptItems(activeRequestId);
        fetchItItems(activeRequestId);
        fetchFinanceItems(activeRequestId);
        fetchHrItems(activeRequestId);
    }, [activeRequestId, fetchDeptItems, fetchItItems, fetchFinanceItems, fetchHrItems]);

    // ---------------- Create form ----------------

    const handleCreateForm = () => setIsModalOpen(true);
    const handleViewForm = () => setView(true);

    const handleModalOk = async () => {
        if (!clearanceData.correspondence_address ||
            !clearanceData.assigned_dept_head ||
            !clearanceData.assigned_it_approver ||
            !clearanceData.assigned_finance_approver ||
            !clearanceData.assigned_hr_approver) {
            message.error('Please fill all required fields');
            return;
        }

        setLoading(true);

        // FIX: pull whatever the user has already typed into the four
        // section tables (below, on the main page) and send it along with
        // the create request, instead of creating an empty request and
        // requiring a second round of edits afterward. Rows with no item
        // name are dropped; a block left completely empty falls back to
        // the backend's default checklist for that section.
        const payload = {
            employee: employee.id,
            emp_no: employee.emp_code,
            designation: employee.designation_name,
            department: employee.department_name,
            joining_date: employee.date_of_joining || null,
            resignation_date: employee.date_of_leaving || null,
            relieving_date: employee.last_working_day || null,
            correspondence_address: clearanceData.correspondence_address,
            assigned_dept_head: clearanceData.assigned_dept_head,
            assigned_it_approver: clearanceData.assigned_it_approver,
            assigned_finance_approver: clearanceData.assigned_finance_approver,
            assigned_hr_approver: clearanceData.assigned_hr_approver,
            dept_items: deptRows
                .filter(r => r.item_name?.trim())
                .map(r => ({ item_name: r.item_name, status: r.status || 'Pending' })),
            it_items: itRows
                .filter(r => r.item_name?.trim())
                .map(r => ({ item_name: r.item_name, response_value: r.status || '' })),
            finance_items: financeRows
                .filter(r => r.item_name?.trim())
                .map(r => ({ item_name: r.item_name, deduction_amount: r.amount || 0 })),
            hr_items: hrRows
                .filter(r => r.item_name?.trim())
                .map(r => ({ item_name: r.item_name, status: r.status || 'Pending' })),
        };

        try {
            await axios.post(`${backUrl}/api/clearance/requests/`, payload, authHeaders);
            message.success('Clearance form created successfully!');
            setIsModalOpen(false);
            setClearanceData({
                correspondence_address: '',
                assigned_dept_head: null,
                assigned_it_approver: null,
                assigned_finance_approver: null,
                assigned_hr_approver: null
            });
            // Clear local drafts — the section-fetch effect (triggered by
            // activeRequestId changing) will repopulate all four tables
            // with the real, saved rows including their backend ids.
            setDeptRows([]);
            setItRows([]);
            setFinanceRows([]);
            setHrRows([]);
            setSectionIds({ dept: null, it: null, finance: null, hr: null });
            await fetchClearanceForm();
        } catch (error) {
            console.error('Error creating clearance form:', error);
            message.error(error.response?.data?.message || 'Error creating clearance form');
        } finally {
            setLoading(false);
        }
    };

    const handleModalCancel = () => setIsModalOpen(false);

    // ---------------- Section approval submits ----------------
    // These mark a section "approved" by its assigned approver and push the
    // current row edits along with it in one PATCH.

    const handleDeptSubmit = async () => {
        if (!activeRequestId) return;
        const payload = {
            handing_over_to_name: employee?.first_name,
            is_employee_signed: true,
            is_receiver_confirmed: true,
            is_approved_by_head: true,
            items: deptRows.map(row => ({ id: row.id, item_name: row.item_name, status: row.status })),
        };
        try {
            await axios.patch(`${backUrl}/api/clearance/dept-section/${activeRequestId}/`, payload, authHeaders);
            message.success('Department clearance submitted successfully!');
            fetchDeptItems(activeRequestId);
            fetchClearanceForm();
        } catch (error) {
            console.error('Error submitting department clearance:', error);
            message.error(error.response?.data?.detail || 'Error submitting department clearance');
        }
    };

    const handleItSubmit = async () => {
        if (!activeRequestId) return;
        const payload = {
            is_approved: true,
            // FIX: field name must match the model (`response_value`).
            items: itRows.map(row => ({ id: row.id, item_name: row.item_name, response_value: row.status })),
        };
        try {
            await axios.patch(`${backUrl}/api/clearance/it-section/${activeRequestId}/`, payload, authHeaders);
            message.success('IT clearance submitted successfully!');
            fetchItItems(activeRequestId);
            fetchClearanceForm();
        } catch (error) {
            console.error('Error submitting IT clearance:', error);
            message.error(error.response?.data?.detail || 'Error submitting IT clearance');
        }
    };

    const handleFinanceSubmit = async () => {
        if (!activeRequestId) return;
        const payload = {
            is_approved: true,
            // FIX: field name must match the model (`deduction_amount`).
            items: financeRows.map(row => ({ id: row.id, item_name: row.item_name, deduction_amount: row.amount || 0 })),
        };
        try {
            await axios.patch(`${backUrl}/api/clearance/finance-section/${activeRequestId}/`, payload, authHeaders);
            message.success('Finance clearance submitted successfully!');
            fetchFinanceItems(activeRequestId);
            fetchClearanceForm();
        } catch (error) {
            console.error('Error submitting finance clearance:', error);
            message.error(error.response?.data?.detail || 'Error submitting finance clearance');
        }
    };

    // HR "save" only persists items + address/phone — it does NOT approve.
    // Approval is a separate, explicit action (see handleHrApprove) so HR
    // can save partial progress without prematurely locking the section.
    const handleHrSave = async () => {
        if (!activeRequestId) return;
        const payload = {
            items: hrRows.map(row => ({ id: row.id, item_name: row.item_name, status: row.status })),
            correspondence_address: formData.address,
            phone_number: formData.phone,
        };
        try {
            await axios.patch(`${backUrl}/api/clearance/hr-section/${activeRequestId}/`, payload, authHeaders);
            message.success('HR clearance items saved!');
            fetchHrItems(activeRequestId);
        } catch (error) {
            console.error('Error saving HR clearance:', error);
            message.error(error.response?.data?.detail || 'Error saving HR clearance');
        }
    };

    // FIX: this is the piece that was entirely missing — nothing in the
    // original code ever called the backend's `hr-approval` action, so a
    // request could never actually reach PENDING_FINAL_APPROVAL / trigger
    // the superuser notification.
    const handleHrApprove = async () => {
        if (!activeRequestId) return;
        try {
            await axios.post(`${backUrl}/api/clearance/requests/${activeRequestId}/hr-approval/`, {}, authHeaders);
            message.success('HR clearance approved and sent for final sign-off!');
            fetchClearanceForm();
        } catch (error) {
            console.error('Error approving HR clearance:', error);
            message.error(error.response?.data?.error || 'Error approving HR clearance');
        }
    };

    const handleFinalApprove = async () => {
        if (!activeRequestId) return;
        try {
            await axios.post(`${backUrl}/api/clearance/requests/${activeRequestId}/final-approve/`, {}, authHeaders);
            message.success('Clearance marked as completed!');
            fetchClearanceForm();
        } catch (error) {
            console.error('Error on final approval:', error);
            message.error(error.response?.data?.error || 'Error on final approval');
        }
    };

    const handleTriggerExitInterview = async () => {
        if (!employee?.id) return;
        setExitInterviewLoading(true);
        try {
            await axios.post(`${backUrl}/api/payroll/exit-interview/trigger/`, { employee: employee.id }, authHeaders);
            // Best-effort: record the flag on the clearance request too, so the
            // button state persists across reloads. Non-fatal if it fails.
            if (activeRequestId) {
                try {
                    await axios.post(`${backUrl}/api/clearance/requests/${activeRequestId}/trigger-exit-interview/`, {}, authHeaders);
                } catch (e) {
                    console.warn('Could not record exit-interview flag on clearance request:', e);
                }
            }
            message.success('Exit Interview Triggered Successfully!');
            fetchClearanceForm();
        } catch (error) {
            console.error('Error triggering exit interview:', error);
            message.error(error.response?.data?.detail || 'Error triggering exit interview');
        } finally {
            setExitInterviewLoading(false);
        }
    };

    // ---------------- Access checks ----------------

    const isDeptHeadAssigned = () => clearanceFormData?.assigned_dept_head === authData?.employee?.id;
    const isItApproverAssigned = () => clearanceFormData?.assigned_it_approver === authData?.employee?.id;
    const isFinanceApproverAssigned = () => clearanceFormData?.assigned_finance_approver === authData?.employee?.id;
    const isHrApproverAssigned = () => clearanceFormData?.assigned_hr_approver === authData?.employee?.id;

    const allPriorSectionsApproved = () =>
        clearanceFormData?.dept_section?.is_approved_by_head &&
        clearanceFormData?.it_section?.is_approved &&
        clearanceFormData?.finance_section?.is_approved;

    // ---------------- Generic row helpers (shared shape across sections) ----------------

    const SECTION_ENDPOINT = { dept: 'dept-section', it: 'it-section', finance: 'finance-section', hr: 'hr-section' };

    // FIX: previously, if the initial section fetch (on mount) hadn't
    // finished yet — or had failed for any reason — `saveRow` gave up
    // immediately with "Section not loaded yet". Now it resolves the
    // section id on demand, and only errors out (with the *real* reason)
    // if that also fails.
    const resolveSectionId = async (sectionKey) => {
        if (sectionIds[sectionKey]) return sectionIds[sectionKey];
        if (!activeRequestId) return null;
        try {
            const res = await axios.get(
                `${backUrl}/api/clearance/${SECTION_ENDPOINT[sectionKey]}/${activeRequestId}/`,
                authHeaders
            );
            setSectionIds(prev => ({ ...prev, [sectionKey]: res.data.id }));
            return res.data.id;
        } catch (error) {
            console.error(`Error resolving ${sectionKey} section id:`, error?.response?.status, error?.response?.data || error.message);
            return null;
        }
    };

    const makeRowHelpers = (rows, setRows, sectionKey, itemsEndpoint, extraField) => ({
        addRow: () => setRows([...rows, { key: String(Date.now()), item_name: '', [extraField]: '' }]),
        deleteRow: (key) => {
            if (rows.length > 1) setRows(rows.filter(row => row.key !== key));
        },
        updateRow: (key, field, value) => setRows(rows.map(row => (row.key === key ? { ...row, [field]: value } : row))),
        saveRow: async (row, backendField) => {
            if (!row.item_name) {
                message.error('Please enter item name');
                return;
            }
            if (!activeRequestId) {
                message.error('No clearance request found for this employee yet. Create the form first.');
                return;
            }
            const sectionId = await resolveSectionId(sectionKey);
            if (!sectionId) {
                message.error(
                    'Could not load this clearance section — you may not have access to it, or it hasn\'t been created on the backend yet. Check the browser console / network tab for the actual status code.'
                );
                return;
            }
            const body = { section: sectionId, item_name: row.item_name, [backendField]: row[extraField] };
            try {
                if (row.id) {
                    await axios.patch(`${backUrl}/api/clearance/${itemsEndpoint}/${row.id}/`, body, authHeaders);
                    message.success('Item updated successfully!');
                } else {
                    const response = await axios.post(`${backUrl}/api/clearance/${itemsEndpoint}/`, body, authHeaders);
                    setRows(rows.map(r => (r.key === row.key ? { ...r, id: response.data.id } : r)));
                    message.success('Item added successfully!');
                }
            } catch (error) {
                console.error(`Error saving item on ${itemsEndpoint}:`, error);
                message.error(error.response?.data?.detail || 'Error saving item');
            }
        },
    });

    const deptHelpers = makeRowHelpers(deptRows, setDeptRows, 'dept', 'dept-items', 'status');
    const itHelpers = makeRowHelpers(itRows, setItRows, 'it', 'it-items', 'status');
    const financeHelpers = makeRowHelpers(financeRows, setFinanceRows, 'finance', 'finance-items', 'amount');
    const hrHelpers = makeRowHelpers(hrRows, setHrRows, 'hr', 'hr-items', 'status');

    // ---------------- Table columns ----------------

    // FIX: before the clearance request exists, rows are just local drafts
    // being assembled for the create payload — there's no backend section
    // to save them to yet, so "Save" would always hit the "not loaded"
    // error. Only show it once the request is real; deleting a draft row
    // is always fine since it's just local state until then.
    const actionColumn = (helpers, rows, backendField) => ({
        title: '',
        key: 'action',
        width: 120,
        className: 'no-print',
        render: (_, record) => (
            <div style={{ display: 'flex', gap: '8px' }}>
                {activeRequestId && (
                    <Button type="primary" size="small" onClick={() => helpers.saveRow(record, backendField)}>
                        Save
                    </Button>
                )}
                <Button
                    type="text"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => helpers.deleteRow(record.key)}
                    disabled={rows.length === 1}
                />
            </div>
        ),
    });

    const deptColumns = [
        { title: 'Sr. No.', key: 'srNo', width: 80, align: 'center', render: (_, __, index) => index + 1 },
        {
            title: 'Particulars', dataIndex: 'item_name', key: 'item_name',
            render: (_, record) => (
                <Input value={record.item_name} onChange={(e) => deptHelpers.updateRow(record.key, 'item_name', e.target.value)} />
            ),
        },
        {
            title: 'Status (Pending/ Completed)', dataIndex: 'status', key: 'status', width: 250,
            render: (_, record) => (
                <Input value={record.status} onChange={(e) => deptHelpers.updateRow(record.key, 'status', e.target.value)} />
            ),
        },
        actionColumn(deptHelpers, deptRows, 'status'),
    ];

    const itColumns = [
        { title: 'Sr. No', key: 'srNo', width: 80, align: 'center', render: (_, __, index) => `${index + 1}.` },
        {
            title: 'List of items', dataIndex: 'item_name', key: 'item_name',
            render: (_, record) => (
                <Input value={record.item_name} onChange={(e) => itHelpers.updateRow(record.key, 'item_name', e.target.value)} />
            ),
        },
        {
            title: 'Status (Returned/ Not returned)', dataIndex: 'status', key: 'status', width: 250,
            render: (_, record) => (
                <Input value={record.status} onChange={(e) => itHelpers.updateRow(record.key, 'status', e.target.value)} />
            ),
        },
        actionColumn(itHelpers, itRows, 'response_value'),
    ];

    const financeColumns = [
        { title: 'Sr. No', key: 'srNo', width: 80, align: 'center', render: (_, __, index) => index + 1 },
        {
            title: 'Item Name', dataIndex: 'item_name', key: 'item_name',
            render: (_, record) => (
                <Input value={record.item_name} onChange={(e) => financeHelpers.updateRow(record.key, 'item_name', e.target.value)} />
            ),
        },
        {
            title: 'Amt. to be deducted in F&F settlement', dataIndex: 'amount', key: 'amount', width: 300,
            render: (_, record) => (
                <Input
                    type="number"
                    value={record.amount}
                    onChange={(e) => financeHelpers.updateRow(record.key, 'amount', e.target.value)}
                />
            ),
        },
        actionColumn(financeHelpers, financeRows, 'deduction_amount'),
    ];

    const hrColumns = [
        { title: 'Sr. No.', key: 'srNo', width: 80, align: 'center', render: (_, __, index) => `${index + 1}.` },
        {
            title: 'List of items', dataIndex: 'item_name', key: 'item_name',
            render: (_, record) => (
                <Input value={record.item_name} onChange={(e) => hrHelpers.updateRow(record.key, 'item_name', e.target.value)} />
            ),
        },
        {
            title: 'Status (Returned/ Not returned)', dataIndex: 'status', key: 'status', width: 250,
            render: (_, record) => (
                <Input value={record.status} onChange={(e) => hrHelpers.updateRow(record.key, 'status', e.target.value)} />
            ),
        },
        actionColumn(hrHelpers, hrRows, 'status'),
    ];

    const statusInfo = clearanceFormData?.status ? STATUS_LABEL[clearanceFormData.status] : null;

    return (
        <>
            {view ? (
                <ViewNocForm employee={employee} requestId={activeRequestId} />
            ) : (
                <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px', background: '#fff' }}>
                    <style>{`
        @media print {
          .no-print { display: none !important; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
          .print-container { padding: 40px !important; }
          .page-break { page-break-before: always; margin-top: 0 !important; padding-top: 0 !important; }
          .ant-table { font-size: 12px !important; }
          .ant-table-thead > tr > th { background: #f0f0f0 !important; font-weight: bold !important; padding: 8px !important; }
          .ant-table-tbody > tr > td { padding: 8px !important; }
          .section-header { background: #f0f0f0 !important; padding: 8px 12px !important; font-weight: bold !important; font-size: 14px !important; margin: 20px 0 10px 0 !important; }
          .signature-line { border-bottom: 1px solid #000 !important; display: inline-block !important; }
          input, textarea { border: none !important; border-bottom: 1px solid #d9d9d9 !important; }
        }
        .page-break { page-break-before: always; }
        .print-container { padding: 20px; }
        .section-header { background: #f0f0f0; padding: 8px 12px; font-weight: bold; font-size: 16px; margin: 20px 0 10px 0; }
        .signature-line { border-bottom: 1px solid #000; display: inline-block; min-width: 200px; margin: 0 10px; }
        .info-row { display: flex; margin-bottom: 12px; flex-direction: column; justify-content: center; }
        .info-item { display: flex; align-items: center; gap: 8px; }
      `}</style>

                    <div className="print-container" id="noc-pdf">

                        {authData?.employee?.department === DEPT_ID.HR && !clearanceFormData && (
                            <Alert
                                className="no-print"
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                                message="Add items to each section below (Dept, IT, Finance, HR), then click 'Create Form' to finalize the request with everything you've entered."
                            />
                        )}

                        <div className="no-print" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            {authData?.employee?.department === DEPT_ID.HR && !clearanceFormData && (
                                <Button type="primary" onClick={handleCreateForm}>Create Form</Button>
                            )}
                            <Button onClick={handleViewForm}>View Form</Button>

                            {statusInfo && <Tag color={statusInfo.color}>{statusInfo.text}</Tag>}

                            {authData?.employee?.department === DEPT_ID.HR &&
                                clearanceFormData?.status === 'PENDING_FINAL_APPROVAL' &&
                                authData?.is_superuser && (
                                    <Button type="primary" style={{ background: '#722ed1' }} onClick={handleFinalApprove}>
                                        Final Approve
                                    </Button>
                                )}

                            <Button
                                onClick={handleTriggerExitInterview}
                                loading={exitInterviewLoading}
                                disabled={clearanceFormData?.exit_interview_triggered}
                            >
                                {clearanceFormData?.exit_interview_triggered ? 'Exit Interview Triggered' : 'Trigger Exit Interview'}
                            </Button>
                        </div>

                        {/* Create Form Modal */}
                        <Modal
                            title="Create Employee Clearance Form"
                            open={isModalOpen}
                            onOk={handleModalOk}
                            onCancel={handleModalCancel}
                            confirmLoading={loading}
                            width={600}
                            okText="Create Request"
                            cancelText="Cancel"
                        >
                            <div style={{ marginTop: 20 }}>
                                <Alert
                                    type="success"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                    message={`${deptRows.length + itRows.length + financeRows.length + hrRows.length} item(s) entered across all sections will be included automatically.`}
                                />
                                <div style={{ marginBottom: 16 }}><Text strong>Employee Name: </Text><Text>{employee?.first_name}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Employee Code: </Text><Text>{employee?.emp_code}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Designation: </Text><Text>{employee?.designation_name}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Department: </Text><Text>{employee?.department_name}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Joining Date: </Text><Text>{employee?.date_of_joining}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Resignation Date: </Text><Text>{employee?.date_of_leaving}</Text></div>
                                <div style={{ marginBottom: 16 }}><Text strong>Relieving Date: </Text><Text>{employee?.last_working_day}</Text></div>

                                <div style={{ marginBottom: 16 }}>
                                    <Text strong style={{ color: 'red' }}>* </Text><Text strong>Correspondence Address:</Text>
                                    <TextArea
                                        rows={3}
                                        value={clearanceData.correspondence_address}
                                        onChange={(e) => setClearanceData({ ...clearanceData, correspondence_address: e.target.value })}
                                        placeholder="Enter correspondence address"
                                        style={{ marginTop: 8 }}
                                    />
                                </div>

                                {/* FIX: assignee dropdowns intentionally show the FULL employee
                                    list for every role (Dept Head, IT, Finance, HR) — no
                                    department-based filtering. Previously a `filterDept` value
                                    was attached to each entry but was never actually applied to
                                    the options list, so this is now made explicit rather than
                                    leaving dead, misleading config in place. */}
                                {[
                                    { label: 'Assign Department Head', field: 'assigned_dept_head' },
                                    { label: 'Assign IT Approver', field: 'assigned_it_approver' },
                                    { label: 'Assign Finance Approver', field: 'assigned_finance_approver' },
                                    { label: 'Assign HR Approver', field: 'assigned_hr_approver' },
                                ].map(({ label, field }) => (
                                    <div style={{ marginBottom: 16 }} key={field}>
                                        <Text strong style={{ color: 'red' }}>* </Text><Text strong>{label}:</Text>
                                        <Select
                                            showSearch
                                            style={{ width: '100%', marginTop: 8 }}
                                            placeholder={`Select ${label.toLowerCase()}`}
                                            optionFilterProp="children"
                                            value={clearanceData[field]}
                                            onChange={(value) => setClearanceData({ ...clearanceData, [field]: value })}
                                            // FIX: `option.children` was an array (JSX with two interpolations:
                                            // `{first_name} - {emp_code}`), so `.toLowerCase()` threw on every
                                            // keystroke and could blank the whole option list. Building a plain
                                            // string up front — and using that as both the label text and the
                                            // thing we filter against — avoids that entirely.
                                            filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        >
                                            {employees.map(emp => {
                                                const label = `${emp.first_name} - ${emp.emp_code}`;
                                                return (
                                                    <Option key={emp.id} value={emp.id} label={label}>{label}</Option>
                                                );
                                            })}
                                        </Select>
                                        {/* {employees.length === 0 && (
                                            <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                                                No employees loaded yet — check the console for the "fetchEmployees" log to confirm the API call is returning data.
                                            </Text>
                                        )} */}
                                    </div>
                                ))}
                            </div>
                        </Modal>

                        {/* Header */}
                        <div className="mt-5">
                            <div style={{ textAlign: 'center', marginBottom: 30 }}>
                                <Title level={3} style={{ margin: 0 }}>{employee?.organisation_name}</Title>
                                <Title level={4} style={{ margin: '10px 0 0 0', fontWeight: 600 }}>Employee Clearance Form</Title>
                            </div>

                            <div style={{ marginBottom: 30, width: '700px' }}>
                                {[
                                    ['Name:', employee.first_name],
                                    ['Employee Code :', employee.emp_code],
                                ].map(([label, value]) => (
                                    <div className="info-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }} key={label}>
                                        <Text strong>{label}</Text>
                                        <Input style={{ width: '400px', border: 'none', borderBottom: '2px solid #000', borderRadius: 0, boxShadow: 'none' }} value={value} bordered={false} disabled />
                                    </div>
                                ))}
                                {[
                                    ['Organisation:', employee.organisation_name],
                                    ['Designation:', employee.designation_name],
                                    ['Department:', employee.department_name],
                                    ['Joining Date:', employee.date_of_joining],
                                ].map(([label, value]) => (
                                    <div className="info-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }} key={label}>
                                        <Text strong>{label}</Text>
                                        <Input style={{ width: '400px', border: 'none', borderBottom: '2px solid #000', borderRadius: 0, boxShadow: 'none' }} value={value} bordered={false} disabled />
                                    </div>
                                ))}
                            </div>

                            <div className="info-row" style={{ width: '700px' }}>
                                {[
                                    ['Resignation Date:', employee.date_of_leaving],
                                    ['Relieving Date:', employee.last_working_day],
                                ].map(([label, value]) => (
                                    <div className="info-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }} key={label}>
                                        <Text strong>{label}</Text>
                                        <Input style={{ width: '400px', border: 'none', borderBottom: '2px solid #000', borderRadius: 0, boxShadow: 'none' }} value={value} bordered={false} disabled />
                                    </div>
                                ))}
                            </div>

                            {/* Department Section */}
                            {/* FIX: was comparing an employee id to a department *name* string —
                                always false unless coincidentally equal. Compare department ids. */}
                            {(authData?.employee?.department === DEPT_ID.HR || authData?.employee?.department === employee?.department) && (
                                <div>
                                    <div className="section-header">CLEARANCE FROM DEPARTMENT HEAD</div>
                                    <Table columns={deptColumns} dataSource={deptRows} pagination={false} bordered size="middle" />
                                    <Button type="dashed" onClick={deptHelpers.addRow} icon={<PlusOutlined />} className="no-print" style={{ marginTop: 10 }}>
                                        Add Row
                                    </Button>
                                    {isDeptHeadAssigned() && (
                                        <Button type="primary" onClick={handleDeptSubmit} className="no-print" style={{ marginTop: 10, marginLeft: 10, background: '#52c41a' }}>
                                            Submit Department Clearance
                                        </Button>
                                    )}
                                    <div style={{ marginTop: 20, lineHeight: '2.5' }}>
                                        <div><Text strong>Employee Signature:</Text><span className="signature-line"></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span></div>
                                        <div><Text strong>Signature of person to whom handing over was done:</Text><span className="signature-line" style={{ minWidth: 150 }}></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span></div>
                                        <div><Text strong>Signature of Departmental Head:</Text><span className="signature-line"></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span></div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <br />

                        {/* IT Section */}
                        {(authData?.employee?.department === DEPT_ID.HR || authData?.employee?.department === DEPT_ID.IT) && (
                            <div className="page-break">
                                <div className="section-header">CLEARANCE FROM - IT</div>
                                <Table columns={itColumns} dataSource={itRows} pagination={false} bordered size="middle" />
                                <Button type="dashed" onClick={itHelpers.addRow} icon={<PlusOutlined />} className="no-print" style={{ marginTop: 10 }}>
                                    Add Row
                                </Button>
                                {isItApproverAssigned() && (
                                    <Button type="primary" onClick={handleItSubmit} className="no-print" style={{ marginTop: 10, marginLeft: 10, background: '#52c41a' }}>
                                        Submit IT Clearance
                                    </Button>
                                )}
                                <div style={{ marginTop: 20, marginBottom: 40 }}>
                                    <Text strong>Signature (IT):</Text><span className="signature-line"></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>
                            </div>
                        )}

                        {/* Finance Section */}
                        {(authData?.employee?.department === DEPT_ID.HR || authData?.employee?.department === DEPT_ID.FINANCE) && (
                            <div className={authData?.employee?.department === DEPT_ID.FINANCE ? 'page-break' : ''}>
                                {authData?.employee?.department === DEPT_ID.FINANCE && (
                                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                                        <Title level={3} style={{ margin: 0 }}>{employee?.organisation_name}</Title>
                                        <Title level={4} style={{ margin: '10px 0 0 0', fontWeight: 600 }}>Employee Clearance Form</Title>
                                    </div>
                                )}
                                <div className="section-header">CLEARANCE FROM FINANCE</div>
                                <Table columns={financeColumns} dataSource={financeRows} pagination={false} bordered size="middle" />
                                <Button type="dashed" onClick={financeHelpers.addRow} icon={<PlusOutlined />} className="no-print" style={{ marginTop: 10 }}>
                                    Add Row
                                </Button>
                                {isFinanceApproverAssigned() && (
                                    <Button type="primary" onClick={handleFinanceSubmit} className="no-print" style={{ marginTop: 10, marginLeft: 10, background: '#52c41a' }}>
                                        Submit Finance Clearance
                                    </Button>
                                )}
                                <div style={{ marginTop: 20 }}>
                                    <Text strong>Signatures: (Finance)</Text><span className="signature-line"></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>
                            </div>
                        )}

                        <br />

                        {/* HR Section */}
                        {authData?.employee?.department === DEPT_ID.HR && (
                            <div className="page-break">
                                <div className="section-header">CLEARANCE FROM HR</div>
                                <Table columns={hrColumns} dataSource={hrRows} pagination={false} bordered size="middle" />
                                <Button type="dashed" onClick={hrHelpers.addRow} icon={<PlusOutlined />} className="no-print" style={{ marginTop: 10 }}>
                                    Add Row
                                </Button>

                                {isHrApproverAssigned() && (
                                    <div className="no-print" style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                                        <Button onClick={handleHrSave}>Save HR Items</Button>
                                        <Button
                                            type="primary"
                                            onClick={handleHrApprove}
                                            style={{ background: '#52c41a' }}
                                            disabled={!allPriorSectionsApproved()}
                                            title={!allPriorSectionsApproved() ? 'Department, IT and Finance must be approved first' : undefined}
                                        >
                                            Approve &amp; Send for Final Sign-off
                                        </Button>
                                    </div>
                                )}

                                <div style={{ marginTop: 30, marginBottom: 20 }}>
                                    <Text strong>
                                        CLEARANCE FROM HR (to be given only after obtaining all the above clearances and completion of exit interview)
                                    </Text>
                                </div>

                                <div style={{ marginBottom: 30 }}>
                                    <Text strong>Signature: (HR)</Text><span className="signature-line"></span><Text strong>Date:</Text><span style={{ marginLeft: 8 }}>{currentDate}</span>
                                </div>

                                <div style={{ border: '1px solid #d9d9d9', padding: 20 }}>
                                    <Text strong style={{ display: 'block', marginBottom: 15 }}>
                                        Address & Phone No. for further Correspondence: (to be given by employee)
                                    </Text>
                                    <TextArea
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        placeholder="Address"
                                        rows={3}
                                        style={{ marginBottom: 10 }}
                                    />
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="Phone No."
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}